import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import useSWRMutation from "swr/mutation";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
  fetcher,
} from "../../../../../Api/swrConfig";
import { toast } from "react-toastify";
import useSWR from "swr";
import Modal from "../../../../../Components/Common/Modal";
import HtmlEditor from "../../../../../Components/Common/HtmlEditor";
import ToggleSwitch from "../../../../../Components/Common/ToggleSwitch";
import Storage from "../../../Storage";
import useToastError from "../../../../../hooks/useErrorToast";
import Button from "../../../../../Components/Common/Button";
import Input from "../../../../../Components/Common/Input";
import {
  NewsApiUrls,
  StaticPageApiUrls,
} from "../../../../../Api/Auth/ApiUrls";
import ImageSelector from "../../../../../Components/Common/ImageSelector";
import Selector from "../../../../../Components/Common/Selector";

const STATIC_PAGES_QUERY = "/static?isStaticPage=true";

const createEmptyAccordion = () => ({
  id: `accordion-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: "",
  content: "",
});

const createEmptyReview = () => ({
  id: `review-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: "",
  role: "",
  image: "",
  rating: 5,
  review: "",
});

const StaticHomeForm = ({ title, customSlug }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [imageField, setImageField] = useState(null);
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[\s]+/g, "-") // Replace spaces with hyphens
      .replace(/[^\w-]+/g, ""); // Remove special characters
  };
  const { showErrorToast } = useToastError();
  const slug = generateSlug(title || "");

  const normalizeServiceSelection = (service) => {
    if (!service) return null;

    if (typeof service === "string") {
      const serviceSlug = generateSlug(service);
      return {
        label: service.replace(/-/g, " "),
        value: serviceSlug,
      };
    }

    const serviceSlug =
      service.value ||
      service.slug ||
      (service.label ? generateSlug(service.label) : null);

    if (!serviceSlug) {
      return null;
    }

    return {
      label: service.label || service.title || serviceSlug.replace(/-/g, " "),
      value: serviceSlug,
      description:
        service.description ||
        service.subtitle ||
        service.headerDescription ||
        "",
      image: service.image || service.thumbnail,
      content: service.content,
      slug: serviceSlug,
    };
  };

  const sanitizeServices = (services) =>
    Array.isArray(services)
      ? services.map((item) => normalizeServiceSelection(item)).filter(Boolean)
      : [];

  const sanitizeAccordions = (accordions) =>
    Array.isArray(accordions)
      ? accordions
          .map((item) => ({
            title: item?.title?.trim() || "",
            content: item?.content?.trim() || "",
          }))
          .filter((item) => item.title || item.content)
      : [];

  const sanitizeReviews = (reviews) =>
    Array.isArray(reviews)
      ? reviews
          .map((item) => ({
            name: item?.name?.trim() || "",
            role: item?.role?.trim() || "",
            image: item?.image || "",
            rating: Math.max(
              0,
              Math.min(
                5,
                typeof item?.rating === "number"
                  ? item.rating
                  : Number(item?.rating) || 0
              )
            ),
            review: item?.review?.trim() || "",
          }))
          .filter((item) => item.name || item.review)
      : [];

  const [formData, setFormData] = useState({
    title: title || "",
    slug: customSlug || slug,
    title1: "",
    header1: "",
    subTitle1: "",
    image1: "",
    title2: "",
    header2: "",
    subTitle2: "",
    image2: "",
    title3: "",
    header3: "",
    subTitle3: "",
    image3: "",
    marquee: "",
    contactTitle: "",
    contactDescription: "",
    contactButtonText: "",
    news: null,
    video1Title: "",
    video1Subtitle: "",
    video1Link: "",
    video2Title: "",
    video2Subtitle: "",
    video2Link: "",
    video3Title: "",
    video3Subtitle: "",
    video3Link: "",
    services: [],
    accordions: [createEmptyAccordion()],
    reviews: [createEmptyReview()],
    showReviews: true,
  });
  const [errors, setErrors] = useState({});
  const { data } = useSWR(slug ? StaticPageApiUrls.info(slug) : null);
  const { data: newsData } = useSWR(NewsApiUrls.list(""));
  const { data: staticPagesList } = useSWR(STATIC_PAGES_QUERY, fetcher);

  const staticPageOptions = useMemo(() => {
    if (!Array.isArray(staticPagesList?.data)) return [];
    return staticPagesList.data
      .filter((item) => item?.slug)
      .map((item) => ({
        label: item?.title || generateSlug(item?.slug).replace(/-/g, " "),
        value: item?.slug,
        description: item?.headerDescription,
        image: item?.image,
        content: item?.content,
      }));
  }, [staticPagesList]);

  useEffect(() => {
    if (data?.data) {
      const content = JSON.parse(data?.data?.content);
      const rawServices = Array.isArray(content?.services)
        ? content.services
        : [
            content?.service1Title
              ? {
                  label: content?.service1Title,
                  value: generateSlug(content?.service1Title),
                  subtitle: content?.service1Subtitle,
                }
              : null,
            content?.service2Title
              ? {
                  label: content?.service2Title,
                  value: generateSlug(content?.service2Title),
                  subtitle: content?.service2Subtitle,
                }
              : null,
            content?.service3Title
              ? {
                  label: content?.service3Title,
                  value: generateSlug(content?.service3Title),
                  subtitle: content?.service3Subtitle,
                }
              : null,
          ].filter(Boolean);
      const services = sanitizeServices(rawServices);
      const accordions = Array.isArray(content?.accordions)
        ? content.accordions
            .map((item, index) => ({
              id:
                item?.id ||
                `accordion-${index}-${Math.random().toString(36).slice(2, 6)}`,
              title: item?.title || item?.question || "",
              content: item?.content || item?.answer || "",
            }))
            .filter((item) => item.title || item.content)
        : [];
      const reviews = Array.isArray(content?.reviews)
        ? content.reviews
            .map((item, index) => {
              const ratingValue =
                typeof item?.rating === "number"
                  ? item.rating
                  : Number(item?.rating);
              return {
                id:
                  item?.id ||
                  `review-${index}-${Math.random().toString(36).slice(2, 6)}`,
                name: item?.name || item?.title || "",
                role: item?.role || item?.subtitle || "",
                image: item?.image || "",
                rating: Math.max(
                  0,
                  Math.min(5, Number.isFinite(ratingValue) ? ratingValue : 5)
                ),
                review: item?.review || item?.content || "",
              };
            })
            .filter((item) => item.name || item.review)
        : [];
      setFormData({
        title: data?.data.title,
        slug: data?.data.slug,
        title1: content?.title1 || "",
        header1: content?.header1 || "",
        subTitle1: content?.subTitle1 || "",
        image1: content?.image1 || "",
        title2: content?.title2 || "",
        header2: content?.header2 || "",
        subTitle2: content?.subTitle2 || "",
        image2: content?.image2 || "",
        title3: content?.title3 || "",
        header3: content?.header3 || "",
        subTitle3: content?.subTitle3 || "",
        image3: content?.image3 || "",
        marquee: content?.marquee || "",
        contactTitle: content?.contactTitle || "",
        contactDescription: content?.contactDescription || "",
        contactButtonText: content?.contactButtonText || "",
        news: content?.news || [],
        video1Title: content?.video1Title || "",
        video1Subtitle: content?.video1Subtitle || "",
        video1Link: content?.video1Link || "",
        video2Title: content?.video2Title || "",
        video2Subtitle: content?.video2Subtitle || "",
        video2Link: content?.video2Link || "",
        video3Title: content?.video3Title || "",
        video3Subtitle: content?.video3Subtitle || "",
        video3Link: content?.video3Link || "",
        services,
        accordions: accordions.length ? accordions : [createEmptyAccordion()],
        reviews: reviews.length ? reviews : [createEmptyReview()],
        showReviews:
          typeof content?.showReviews === "boolean" ? content.showReviews : true,
      });
    }
  }, [data]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.slug) newErrors.slug = "Slug is required.";

    // Add more validation logic as needed
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    setFormData((prevState) => {
      const updatedData = { ...prevState, [name]: value };

      if (name === "title") {
        updatedData.slug = generateSlug(value); // Use `value` instead of `formData.title`
      }

      if (name === "services") {
        updatedData.services = sanitizeServices(value);
      }

      if (name === "accordions") {
        updatedData.accordions = sanitizeAccordions(value).map(
          (item, index) => ({
            id:
              prevState.accordions?.[index]?.id ||
              `accordion-${index}-${Math.random().toString(36).slice(2, 6)}`,
            ...item,
          })
        );
      }

      if (name === "reviews") {
        updatedData.reviews = sanitizeReviews(value).map((item, index) => ({
          id:
            prevState.reviews?.[index]?.id ||
            `review-${index}-${Math.random().toString(36).slice(2, 6)}`,
          ...item,
        }));
      }

      return updatedData;
    });
  };

  const handleAccordionChange = (id, key, value) => {
    setFormData((prev) => ({
      ...prev,
      accordions: prev.accordions.map((item) =>
        item.id === id ? { ...item, [key]: value } : item
      ),
    }));
  };

  const addAccordionItem = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setFormData((prev) => ({
      ...prev,
      accordions: [...prev.accordions, createEmptyAccordion()],
    }));
  };

  const removeAccordionItem = (id) => {
    setFormData((prev) => {
      const remaining = prev.accordions.filter((item) => item.id !== id);
      return {
        ...prev,
        accordions: remaining.length ? remaining : [createEmptyAccordion()],
      };
    });
  };

  const handleReviewChange = (id, key, value) => {
    setFormData((prev) => ({
      ...prev,
      reviews: prev.reviews.map((item) =>
        item.id === id ? { ...item, [key]: value } : item
      ),
    }));
  };

  const addReviewItem = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setFormData((prev) => ({
      ...prev,
      reviews: [...prev.reviews, createEmptyReview()],
    }));
  };

  const removeReviewItem = (id) => {
    setFormData((prev) => {
      const remaining = prev.reviews.filter((item) => item.id !== id);
      return {
        ...prev,
        reviews: remaining.length ? remaining : [createEmptyReview()],
      };
    });
  };

  const { trigger, isMutating } = useSWRMutation(
    StaticPageApiUrls.edit(formData.slug),
    sendPutRequestAuth,
    {
      onSuccess: () => {
        toast.success("Success");
      },
      onError: (error) => {
        if (error.response.data.message === "Static Page not found.") {
          toast.warn("Static Page not found. Creating a new one.");
          if (!validateForm()) return;
          const modifiedData = {
            title: formData.title,
            slug: formData.slug,
            content: JSON.stringify({
              title1: formData.title1,
              header1: formData.header1,
              subTitle1: formData.subTitle1,
              image1: formData.image1,
              title2: formData.title2,
              header2: formData.header2,
              subTitle2: formData.subTitle2,
              image2: formData.image2,
              title3: formData.title3,
              header3: formData.header3,
              subTitle3: formData.subTitle3,
              image3: formData.image3,
              marquee: formData.marquee,
              contactTitle: formData.contactTitle,
              contactDescription: formData.contactDescription,
              contactButtonText: formData.contactButtonText,
              news: formData.news,
              video1Title: formData.video1Title,
              video1Subtitle: formData.video1Subtitle,
              video1Link: formData.video1Link,
              video2Title: formData.video2Title,
              video2Subtitle: formData.video2Subtitle,
              video2Link: formData.video2Link,
              video3Title: formData.video3Title,
              video3Subtitle: formData.video3Subtitle,
              video3Link: formData.video3Link,
              services: sanitizeServices(formData.services),
              accordions: sanitizeAccordions(formData.accordions),
              reviews: sanitizeReviews(formData.reviews),
              showReviews: formData.showReviews,
            }),
          };

          staticPageCreateTrigger(modifiedData);
        } else {
          showErrorToast(error);
        }
      },
    }
  );

  const { trigger: staticPageCreateTrigger, isMutating: createMutating } =
    useSWRMutation(StaticPageApiUrls.create, sendPostRequestAuth, {
      onSuccess: () => {
        toast.success("Success");
      },
      onError: (error) => {
        showErrorToast(error);
      },
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const modifiedData = {
      title: formData.title,
      content: JSON.stringify({
        title1: formData.title1,
        header1: formData.header1,
        subTitle1: formData.subTitle1,
        image1: formData.image1,
        title2: formData.title2,
        header2: formData.header2,
        subTitle2: formData.subTitle2,
        image2: formData.image2,
        title3: formData.title3,
        header3: formData.header3,
        subTitle3: formData.subTitle3,
        image3: formData.image3,
        marquee: formData.marquee,
        contactTitle: formData.contactTitle,
        contactDescription: formData.contactDescription,
        contactButtonText: formData.contactButtonText,
        news: formData.news,
        video1Title: formData.video1Title,
        video1Subtitle: formData.video1Subtitle,
        video1Link: formData.video1Link,
        video2Title: formData.video2Title,
        video2Subtitle: formData.video2Subtitle,
        video2Link: formData.video2Link,
        video3Title: formData.video3Title,
        video3Subtitle: formData.video3Subtitle,
        video3Link: formData.video3Link,
        services: sanitizeServices(formData.services),
        accordions: sanitizeAccordions(formData.accordions),
        reviews: sanitizeReviews(formData.reviews),
        showReviews: formData.showReviews,
      }),
    };
    delete modifiedData.slug;
    trigger(modifiedData);
  };

  return (
    <div className="mt-4">
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <Input
          label="Page title"
          name="title"
          required
          disabled={title !== ""}
          placeholder="Enter page title"
          onChange={handleInputChange}
          error={errors.title}
          value={formData.title}
        />
        <Input
          label="Page slug"
          name="slug"
          required
          placeholder="Enter page slug"
          onChange={handleInputChange}
          error={errors.slug}
          value={formData.slug}
          disabled
        />
        <Input
          label="Marquee"
          name="marquee"
          placeholder="Enter marquee text"
          onChange={handleInputChange}
          error={errors.marquee}
          value={formData.marquee}
        />
        <h2 className="font-medium">Slide 1</h2>
        {/* <Input
          label="Title"
          name="title1"
          placeholder="Enter Slide 1 title"
          onChange={handleInputChange}
          error={errors.title1}
          value={formData.title1}
        /> */}
        <HtmlEditor
          label="Header"
          name="header1"
          placeholder="Enter Slide 1 header"
          onChange={handleInputChange}
          error={errors.header1}
          value={formData.header1}
        />
        <HtmlEditor
          label="Subtitle"
          name="subTitle1"
          placeholder="Enter Slide 1 subtitle"
          onChange={handleInputChange}
          error={errors.subTitle1}
          value={formData.subTitle1}
        />
        <ImageSelector
          label="Upload Slide 1 Image"
          name="image1"
          setModalOpen={setModalOpen}
          onChange={handleInputChange}
          value={formData.image1}
          onClick={() => {
            setImageField("image1");
          }}
        />

        <h2 className="font-medium">Slide 2</h2>
        {/* <Input
          label="Title"
          name="title2"
          placeholder="Enter Slide 2 title"
          onChange={handleInputChange}
          error={errors.title2}
          value={formData.title2}
        /> */}
        <HtmlEditor
          label="Header"
          name="header2"
          placeholder="Enter Slide 2 header"
          onChange={handleInputChange}
          error={errors.header2}
          value={formData.header2}
        />
        <HtmlEditor
          label="Subtitle"
          name="subTitle2"
          placeholder="Enter Slide 2 subtitle"
          onChange={handleInputChange}
          error={errors.subTitle2}
          value={formData.subTitle2}
        />
        <ImageSelector
          label="Upload Slide 2 Image"
          name="image2"
          setModalOpen={setModalOpen}
          onClick={() => {
            setImageField("image2");
          }}
          onChange={handleInputChange}
          value={formData.image2}
        />
        <h2 className="font-medium">Slide 3</h2>
        {/* <Input
          label="Title"
          name="title3"
          placeholder="Enter Slide 3 title"
          onChange={handleInputChange}
          error={errors.title3}
          value={formData.title3}
        /> */}
        <HtmlEditor
          label="Header"
          name="header3"
          placeholder="Enter Slide 3 header"
          onChange={handleInputChange}
          error={errors.header3}
          value={formData.header3}
        />
        <HtmlEditor
          label="Subtitle"
          name="subTitle3"
          placeholder="Enter Slide 3 subtitle"
          onChange={handleInputChange}
          error={errors.subTitle3}
          value={formData.subTitle3}
        />
        <ImageSelector
          label="Upload Slide 3 Image"
          name="image3"
          setModalOpen={setModalOpen}
          onChange={handleInputChange}
          value={formData.image3}
          onClick={() => {
            setImageField("image3");
          }}
        />
        <Selector
          name="news"
          label="Select News"
          value={formData?.news}
          options={newsData?.data?.map((item) => ({
            label: item.title,
            value: item._id,
            content: JSON.stringify({
              link: item.link,
              description: item.description,
              category: item.category.title,
              slug: item.slug,
            }),
          }))}
          isClearable={false}
          onChange={handleInputChange}
          error={errors?.news}
          multiple
          isSearchable
          // isLoading={isLoading}
        />
        <h2 className="font-medium">YouTube Videos</h2>
        <h3 className="font-normal">Video 1</h3>
        <Input
          label="Title"
          name="video1Title"
          placeholder="Enter Video 1 title"
          onChange={handleInputChange}
          error={errors.video1Title}
          value={formData.video1Title}
        />
        <Input
          label="Subtitle"
          name="video1Subtitle"
          placeholder="Enter Video 1 subtitle"
          onChange={handleInputChange}
          error={errors.video1Subtitle}
          value={formData.video1Subtitle}
        />
        <Input
          label="Youtube Link"
          name="video1Link"
          placeholder="Enter Video 1 link"
          onChange={handleInputChange}
          error={errors.video1Link}
          value={formData.video1Link}
        />
        <h3 className="font-normal">Video 2</h3>
        <Input
          label="Title"
          name="video2Title"
          placeholder="Enter Video 2 title"
          onChange={handleInputChange}
          error={errors.video2Title}
          value={formData.video2Title}
        />
        <Input
          label="Subtitle"
          name="video2Subtitle"
          placeholder="Enter Video 2 subtitle"
          onChange={handleInputChange}
          error={errors.video2Subtitle}
          value={formData.video2Subtitle}
        />
        <Input
          label="Youtube Link"
          name="video2Link"
          placeholder="Enter Video 2 link"
          onChange={handleInputChange}
          error={errors.video2Link}
          value={formData.video2Link}
        />
        <h3 className="font-normal">Video 3</h3>
        <Input
          label="Title"
          name="video3Title"
          placeholder="Enter Video 3 title"
          onChange={handleInputChange}
          error={errors.video3Title}
          value={formData.video3Title}
        />
        <Input
          label="Subtitle"
          name="video3Subtitle"
          placeholder="Enter Video 3 subtitle"
          onChange={handleInputChange}
          error={errors.video3Subtitle}
          value={formData.video3Subtitle}
        />
        <Input
          label="Youtube Link"
          name="video3Link"
          placeholder="Enter Video 3 link"
          onChange={handleInputChange}
          error={errors.video3Link}
          value={formData.video3Link}
        />
        <h2 className="font-medium">Our Services</h2>
        <Selector
          name="services"
          label="Select services"
          value={formData.services}
          options={staticPageOptions}
          onChange={handleInputChange}
          error={errors.services}
          multiple
          isSearchable
          isClearable
        />
        <div className="flex items-center justify-between mt-4">
          <h2 className="font-medium">FAQ Items</h2>
          <Button type="outline" size="small" onClick={addAccordionItem}>
            Add FAQ
          </Button>
        </div>
        <div className="flex flex-col gap-4">
          {formData.accordions.map((item) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-lg p-4 flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-700">FAQ</h3>
                <button
                  type="button"
                  onClick={() => removeAccordionItem(item.id)}
                  disabled={formData.accordions.length === 1}
                  className="text-red-500 hover:text-red-600 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
              <HtmlEditor
                label="FAQ Question"
                name="accordionTitle"
                placeholder="Enter FAQ question"
                value={item.title}
                onChange={(_, value) =>
                  handleAccordionChange(item.id, "title", value)
                }
              />
              <HtmlEditor
                label="FAQ Answer"
                name="accordionContent"
                placeholder="Enter FAQ answer"
                value={item.content}
                onChange={(_, value) =>
                  handleAccordionChange(item.id, "content", value)
                }
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
          <h2 className="font-medium">Student Reviews</h2>
          <ToggleSwitch
            name="showReviews"
            value={formData.showReviews}
            onChange={handleInputChange}
          />
          </div>
          <Button type="outline" size="small" onClick={addReviewItem}>
            Add review
          </Button>
        </div>
        <div className="flex flex-col gap-4">
          {formData.reviews.map((item) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-lg p-4 flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-700">
                  Review
                </h3>
                <button
                  type="button"
                  onClick={() => removeReviewItem(item.id)}
                  disabled={formData.reviews.length === 1}
                  className="text-red-500 hover:text-red-600 disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
              <Input
                label="Name"
                name="reviewName"
                placeholder="Enter reviewer name"
                value={item.name}
                onChange={(_, value) =>
                  handleReviewChange(item.id, "name", value)
                }
              />
              <Input
                label="Role"
                name="reviewRole"
                placeholder="Enter reviewer role"
                value={item.role}
                onChange={(_, value) =>
                  handleReviewChange(item.id, "role", value)
                }
              />
              <ImageSelector
                label="Reviewer Image"
                name={`review:${item.id}`}
                setModalOpen={setModalOpen}
                onChange={(_, value) =>
                  handleReviewChange(
                    item.id,
                    "image",
                    Array.isArray(value) ? value[0] || "" : value || ""
                  )
                }
                value={item.image}
                onClick={() => {
                  setImageField(`review:${item.id}`);
                }}
              />
              <Input
                label="Rating (0-5)"
                name="reviewRating"
                type="number"
                min="0"
                max="5"
                step="0.1"
                placeholder="Enter reviewer rating"
                value={item.rating}
                onChange={(_, value) =>
                  handleReviewChange(item.id, "rating", Number(value) || 0)
                }
              />
              <Input
                label="Review"
                name="reviewText"
                placeholder="Enter review text"
                value={item.review}
                onChange={(_, value) =>
                  handleReviewChange(item.id, "review", value)
                }
              />
            </div>
          ))}
        </div>
        <h2 className="font-medium">Contact Section</h2>
        <Input
          label="Title"
          name="contactTitle"
          placeholder="Enter contact section title"
          onChange={handleInputChange}
          error={errors.contactTitle}
          value={formData.contactTitle}
        />
        <HtmlEditor
          label="Description"
          name="contactDescription"
          placeholder="Enter contact section description"
          onChange={handleInputChange}
          error={errors.contactDescription}
          value={formData.contactDescription}
        />
        <Input
          label="Button Text"
          name="contactButtonText"
          placeholder="Enter contact button text"
          onChange={handleInputChange}
          error={errors.contactButtonText}
          value={formData.contactButtonText}
        />
        <div className="flex justify-end gap-4">
          <Button
            onClick={handleSubmit}
            type="primary"
            size="small"
            isLoading={isMutating || createMutating}
          >
            Confirm
          </Button>
        </div>
      </form>
      <Modal
        isOpen={isModalOpen}
        title={"Storage Manager"}
        // onConfirm={handleSubmit}
        onCancel={() => {
          setModalOpen(false);
        }}
        footer={false}
      >
        <Storage
          isSelector
          onConfirm={(file) => {
            if (Array.isArray(file) && file.length) {
              const fileUrl = file[0]?.fileUrl;
              if (imageField?.startsWith("review:")) {
                const reviewId = imageField.split(":")[1];
                handleReviewChange(reviewId, "image", fileUrl);
              } else if (imageField) {
                handleInputChange(imageField, fileUrl);
              }
            }
            setModalOpen(false);
            setImageField(null);
          }}
        />
      </Modal>
    </div>
  );
};

export default StaticHomeForm;
